import { FormHelperText } from '@mui/material';
import { AES, enc } from 'crypto-js';
import moment from 'moment-timezone';
import * as Yup from 'yup';
import { BASEAPI } from 'src/api/api';
import STATE_LIST from 'src/utils/stateList.json';
import {
  DATE_FORMAT, NUMBER_TYPE_ERROR_MSG, STORAGE_KEY, USER_ROLES, USER_TYPES
} from './constants';
import { formatInTimezone, getStoredUserTimezone } from './timezoneUtils';
import { formatVolumeValue } from './volumeUtils';

export const loginValidationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Must be a valid email')
    .max(80)
    .required('Email is required'),
  password: Yup.string().max(40).required('Password is required')
});

export const getAvatarName = (name = '') => name
  .replace(/\s+/, ' ')
  .split(' ')
  .slice(0, 2)
  .map((v) => v && v[0].toUpperCase())
  .join('');

const encryptData = (data) => {
  let value = data;
  if (typeof data !== 'object') {
    value = JSON.stringify(data);
  }
  const ciphertext = AES.encrypt(
    value,
    process.env.REACT_APP_SECRET_KEY
  ).toString();
  return ciphertext;
};

const decryptData = (ciphertext) => {
  if (!ciphertext) return null;
  try {
    const bytes = AES.decrypt(ciphertext, process.env.REACT_APP_SECRET_KEY);
    const originalText = bytes.toString(enc.Utf8);
    if (!originalText) return null;
    return originalText.replace(/["]+/g, ''); // to remove double quotes ;
  } catch (error) {
    console.warn('Unable to decrypt local data', error);
    return null;
  }
};

export const storeLocalData = (key, data, encrypt) => {
  let dataToStore = data;
  if (typeof data === 'object') {
    dataToStore = JSON.stringify(data);
  }
  if (encrypt) {
    dataToStore = encryptData(dataToStore);
  }
  // console.log('data to store', key, dataToStore);
  localStorage.setItem(key, dataToStore);
};

export const getLocalData = (key, parse, decrypt) => {
  let dataToFetch = localStorage.getItem(key);
  if (!dataToFetch) return null;
  try {
    if (decrypt) {
      dataToFetch = decryptData(dataToFetch);
      if (!dataToFetch) {
        localStorage.removeItem(key);
        return null;
      }
    }
    if (parse) {
      dataToFetch = JSON.parse(dataToFetch);
    }
    // console.log('data To fetch', key, dataToFetch);
    return dataToFetch;
  } catch (error) {
    localStorage.removeItem(key);
    console.warn(`Unable to read local data for key: ${key}`, error);
    return null;
  }
};

export const clearLocalData = () => {
  localStorage.clear();
  delete BASEAPI.defaults.headers.common.Authorization;
};

export const getStateName = (stateId) => {
  const selectedState = STATE_LIST.find((state) => state.id === stateId);
  return selectedState ? selectedState.name : (stateId || '');
};

export const updateItemInArray = (dataList, item) => {
  const updatedDataList = dataList.map((data) => {
    if (data.id === item.id) {
      return item;
    }
    return data;
  });
  return updatedDataList;
};

export const renderHelperText = (meta) => {
  const { touched, error } = meta;
  if (touched && error) {
    return <FormHelperText>{error}</FormHelperText>;
  }
  return null;
};

export const getGroupName = (groupType, data) => {
  const selectedGroup = data.find((group) => group.id === groupType);
  return selectedGroup.name || 'NA';
};

export const getMinLimitErrorMsg = (field, limit) => {
  const errorMsg = `${field.label} must be more than or equal to ${limit}`;
  return errorMsg;
};

export const getMaxLimitErrorMsg = (field, limit) => {
  const errorMsg = `${field.label} must be less than or equal to ${limit}`;
  return errorMsg;
};

export const getMaxLengthErrorMsg = (field, limit) => {
  const errorMsg = `${field.label} must be at most ${limit} characters`;
  return errorMsg;
};

export const getRangeErrorValidation = (field, min, max) => Yup.number()
  .typeError(NUMBER_TYPE_ERROR_MSG)
  .min(min, getMinLimitErrorMsg(field, min))
  .max(max, getMaxLimitErrorMsg(field, max))
  .required(field.requiredErrorMsg);

export const getFormattedDate = (timestamp, format = DATE_FORMAT.dateTimeString) => (
  formatInTimezone(timestamp, format, getStoredUserTimezone()) || 'NA'
);

export const getFormattedDateOnly = (date, format = DATE_FORMAT.date) => {
  const parsedDate = moment.utc(date, 'YYYY-MM-DD', true);
  return parsedDate.isValid() ? parsedDate.format(format) : 'NA';
};

export const getFormattedDateTime = (timestamp) => {
  if (!timestamp) return null;
  const m = moment(timestamp).tz(getStoredUserTimezone());
  if (!m.isValid()) return null;
  return {
    date: m.format(DATE_FORMAT.date),
    time: m.format('hh:mm:ss A')
  };
};

export const getFormattedDateTime12Hour = (timestamp, withSeconds = false) => {
  if (!timestamp) return null;
  return formatInTimezone(
    timestamp,
    withSeconds ? 'DD/MM/YYYY hh:mm:ss A' : DATE_FORMAT.dateTimeString,
    getStoredUserTimezone()
  );
};

export const getMileageType = (vehicleType) => {
  const mileageType = vehicleType === 'Movable' ? 'KM/L' : 'L/Hr';
  return mileageType;
};

export const getMileageUnit = (vehicleType) => {
  const mileageType = vehicleType === 'Movable' ? 'KM' : 'Hr';
  return mileageType;
};

export const getFuelQuantity = (value, configuredValue, volumeUnit) => {
  let activeVolumeUnit = volumeUnit;
  if (activeVolumeUnit === undefined) {
    activeVolumeUnit = getLocalData(STORAGE_KEY.user, true)?.fuel_volume_unit;
  }
  return formatVolumeValue(value || 0, configuredValue, activeVolumeUnit);
};

export const getFuelPrice = (value) => {
  let fuelPrice = '0 Rs';
  if (value) {
    fuelPrice = `${value.toFixed(2)} Rs`;
  }
  return fuelPrice;
};

export const isAdminUser = (getUser) => {
  const user = getUser();
  const isAdmin = user?.role === USER_ROLES.admin;
  return isAdmin;
};

export const hasAdminLikeAccess = (getUser) => {
  const user = getUser();
  return user?.role === USER_ROLES.admin
    || user?.role === USER_ROLES.siteManager;
};

export const getSelectedSiteFilterValue = (siteValue) => (
  siteValue === 'all' ? null : siteValue
);

export const getBalanceValue = (tank) => {
  let balance = (tank.current_balance.stock / tank.max_volume) * 100;
  if (balance > 100) balance = 100;
  return balance ? Number(balance.toFixed(0)) : 0;
};

function getDateTimeFilterValue(dateValue) {
  let filterDate = { lte: null, gte: null };
  if (dateValue && typeof dateValue === 'object' && ('start' in dateValue || 'end' in dateValue)) {
    return {
      lte: dateValue.end || null,
      gte: dateValue.start || null
    };
  }
  if (dateValue) {
    filterDate = {
      lte: `${dateValue}T23:59:59`, // end time of the date
      gte: `${dateValue}T00:00:00` // start time of the date
    };
  }
  return filterDate;
}

export const getTransactionQueryParams = (
  isSales,
  searchValue,
  dateValue,
  siteValue,
  transactionType,
  tankValue = 'all'
) => {
  const selectedSite = getSelectedSiteFilterValue(siteValue);
  const selectedTank = transactionType === 'iot'
    ? getSelectedSiteFilterValue(tankValue)
    : null;
  const filterDate = getDateTimeFilterValue(dateValue);
  if (transactionType === 'external') {
    return {
      vehicle_number: searchValue,
      site: selectedSite,
      date_time__lte: filterDate.lte,
      date_time__gte: filterDate.gte
    };
  }
  const queryParams = isSales
    ? {
      vehicle_number: searchValue,
      site: selectedSite,
      tank: selectedTank,
      start_time__lte: filterDate.lte,
      start_time__gte: filterDate.gte
    }
    : {
      invoice_number: searchValue,
      site: selectedSite,
      order_date__lte: filterDate.lte,
      order_date__gte: filterDate.gte
    };
  return queryParams;
};

export const getSensorTransactionQueryParams = (searchValue, dateValue, siteValue) => {
  const selectedSite = getSelectedSiteFilterValue(siteValue);
  const filterDate = getDateTimeFilterValue(dateValue);
  const queryParams = {
    vehicle__registration_num: searchValue,
    site: selectedSite,
    filled_time__gte: filterDate.gte,
    filled_time__lte: filterDate.lte
  };
  return queryParams;
};

export const getMileageReportQueryParams = (searchText, siteFilter, groupFilter, dateValue) => {
  const selectedSite = getSelectedSiteFilterValue(siteFilter);
  const selectedGroup = groupFilter === 'all' ? null : groupFilter;
  const filterDate = getDateTimeFilterValue(dateValue);
  const queryParams = {
    vehicle_number: searchText,
    site: selectedSite,
    vehicle_group: selectedGroup,
    start_time__gte: filterDate.gte,
    start_time__lte: filterDate.lte
  };
  return queryParams;
};

export const getReconciliationQueryParams = (
  searchText,
  siteFilter,
  dispenserFilter,
  dateValue
) => {
  const selectedSite = getSelectedSiteFilterValue(siteFilter);
  const selectedDispenser = getSelectedSiteFilterValue(dispenserFilter);
  const queryParams = {
    dispenser__station_name: searchText,
    dispenser__site: selectedSite,
    dispenser: selectedDispenser,
    date__gte: dateValue,
    date__lte: dateValue
  };
  return queryParams;
};

export const getCardLimitRequestQueryParams = (searchText, dateValue, siteValue) => {
  const selectedSite = getSelectedSiteFilterValue(siteValue);
  const filterDate = getDateTimeFilterValue(dateValue);
  const queryParams = {
    search: searchText,
    site: selectedSite,
    requested_on__gte: filterDate.gte,
    requested_on__lte: filterDate.lte
  };
  return queryParams;
};

export const camelCaseString = (value) => value?.replace(/^./, (str) => str?.toUpperCase());

export const removeLeadingZeros = (value) => value?.replace(/^0+/, '') || 'NA';

export const getUserRole = (role) => {
  const userRole = USER_TYPES.find((item) => item.id === role);
  return userRole?.name || '';
};

export const formatNumber = (value) => {
  let formattedValue = 0;
  if (value) {
    formattedValue = value.toFixed(2);
  }
  return formattedValue;
};
